import { safeRedirectPath } from "~/utils/safeRedirect";
import { onboardingRedirect } from "~/utils/onboarding";
import { codeOfTag } from "~/utils/locale";

const LOGGING = false;
// The language a person chose is applied once per visit: a choice made afterwards in this tab is not undone.
let languageApplied = false;

let user = reactive({});
let account = reactive({
  balance: 0,
  displayname: "",
  email: "",
  id: "",
  minimal_balance: 0,
  minimal_credit: 0,
  subscription_plan: null
});

// The account fields the dashboard reads from `$account`, copied from the auth status on each navigation and when the
// status refreshes (the watch below used to forget `options`).
const ACCOUNT_FIELDS = ["balance", "displayname", "email", "id", "minimal_balance", "minimal_credit", "subscription_plan", "options"];
const copyAccount = (source) => {
  for (const field of ACCOUNT_FIELDS) account[field] = source?.[field];
};

export default defineNuxtRouteMiddleware(async (to, from) => {
  LOGGING && console.log("--------------------");
  if (to.meta?.auth === false) {
    LOGGING && console.log(`${ from.path } -> ${ to.path } is not protected by auth`);
    return true;
  } else LOGGING && console.log(`[ROUTER] ${ from.path } -> ${ to.path }`);
  const auth = useAuth(); // Use your useAuth composable
  // Wait for the authentication status check
  await auth.refreshAuthStatus();
  const authStatus = auth.getAuthStatus();

  LOGGING && console.log("[AUTH] Auth status:", authStatus.status);

  if (authStatus.status === "unauthenticated" && to.path !== "/login") {
    // If the user is not authenticated and is not on the login page,
    // redirect to the login page
    setPageLayout("clean");
    // The MCP server's OAuth consent screen must survive the login: we come back to it afterwards
    const redirect = safeRedirectPath(to.fullPath);
    return navigateTo(redirect ? { path: "/login", query: { redirect } } : "/login");
  }

  if (to.path?.startsWith("/management") && !authStatus.data?.user?.isAdmin) {
    setPageLayout("default");
    return navigateTo("/");
  } else if (to.path?.startsWith("/management")) setPageLayout("management");

  const nuxtApp = useNuxtApp();
  if (authStatus.data?.printer) {
    if (!nuxtApp.$printer) nuxtApp.provide("printer", authStatus.data?.printer);
    setPageLayout("printers");
    if (!to.path?.startsWith("/printers")) {
      return navigateTo("/printers");
    }
  } else {
    copyAccount(authStatus.data?.account);
  }
  if (!nuxtApp.$account && !authStatus.data?.printer) {
    nuxtApp.provide("account", account);
    watch(() => authStatus.data, (newVal) => copyAccount(newVal?.account));
  }

  if (!authStatus.data?.printer) {
    // The onboarding, in its order: confirm the address, answer the questions, choose a plan (its free trial counts).
    // Until then the dashboard stays closed (utils/onboarding.js, onboardingRedirect).
    const redirect = onboardingRedirect({
      emailVerified: authStatus.data?.user?.emailVerified,
      hasPlan: !!authStatus.data?.account?.subscription_plan,
      isAdmin: !!authStatus.data?.user?.isAdmin
    }, to.path);
    if (redirect && redirect !== to.path) return navigateTo(redirect);
  }

  if (!nuxtApp.$user) nuxtApp.provide("user", authStatus.data?.user);

  // The language chosen on any device (`users.locale`) wins over this browser's: the dashboard speaks it from sign-in.
  const chosen = codeOfTag(authStatus.data?.user?.language);
  if (!languageApplied && chosen) {
    languageApplied = true;
    if (nuxtApp.$i18n.locale.value !== chosen) await nuxtApp.$i18n.setLocale(chosen);
  }

  nuxtApp.$posthog?.()?.identify(nuxtApp.$user.id, {
    email: nuxtApp.$user.email,
    firstname: nuxtApp.$user.firstname,
    lastname: nuxtApp.$user.lastname,
    name: `${ nuxtApp.$user.firstname } ${ nuxtApp.$user.lastname }`,
    is_admin: !!nuxtApp.$user.isAdmin
  });

  return true;
});
